import React from 'react'
import { useSelector } from 'react-redux'

import { Box, Flex, Icon, Table, Tooltip, useTableState } from '@driftt/dds'
import { faCircleInfo } from '@driftt/dds.icons/light/faCircleInfo'

import ActivityActions, { ActionType } from 'components/ActivityActions'
import { driftErrorStateBuilder, driftLoadingStateBuilder } from 'components/States'
import { useAccountContactActivity } from 'hooks/useAccountContactActivity'
import usePresenceSubscription from 'hooks/usePresenceSubscription'
import { ContactWithActivites } from 'modules/accountActivity/domain'
import { EmptyContactsComponent } from 'pages/CCRMAccountDetailsPage/ContactsTab'
import { getCurrentOrg } from 'selectors'
import { humanizedRelativeTime } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import { ContactTile } from './ContactTile'
import { EngageScoreBadge } from './EngageScoreBadge'

interface ContactsTabProps {
  accountId: string
  trackAction: (action: string) => void
}

const ContactsTab: React.FC<ContactsTabProps> = ({ accountId, trackAction }) => {
  const { tableState, handleTableStateChange } = useTableState<ContactWithActivites>({
    defaultPagination: {
      pageIndex: 0,
      pageSize: 10
    }
  })

  const {
    response: accountContacts,
    count,
    isLoading,
    accountContactsError
  } = useAccountContactActivity(
    accountId,
    tableState.pagination.pageSize,
    tableState.pagination.pageIndex + 1
  )

  const org = useSelector(getCurrentOrg)
  const contacts = accountContacts?.data?.map(contact => contact.endUserData) ?? []
  const presentEndUserIds = usePresenceSubscription(org.id, contacts)

  const ErrorComponent = driftErrorStateBuilder("this account's contacts")
  const LoadingComponent = driftLoadingStateBuilder('Loading contacts')

  if (accountContactsError) {
    segmentTrack(TrackType.DRAWER_ERROR, {
      error: accountContactsError,
      source: 'Contacts Tab',
      accountId
    })
  }

  return (
    <Flex
      direction="column"
      fullHeight
      fullWidth
      css={{
        backgroundColor: '$gray2',
        padding: '16px',
        height: 'calc(100% - 31px)',
        overflow: 'scroll'
      }}>
      {isLoading && <LoadingComponent />}
      {!isLoading && accountContactsError && <ErrorComponent />}
      {!isLoading &&
        !accountContactsError &&
        (!accountContacts || !accountContacts?.data || count === 0) && <EmptyContactsComponent />}
      {!isLoading &&
        !accountContactsError &&
        accountContacts &&
        accountContacts.data &&
        accountContacts.data.length > 0 && (
          <Table
            rows={accountContacts.data}
            columns={[
              {
                value: 'contactName',
                header: 'Name',
                cell: contact => (
                  <ContactTile
                    contact={contact}
                    trackAction={trackAction}
                    isAvailable={presentEndUserIds.has(contact.endUserId)}
                  />
                ),
                size: 130
              },
              {
                value: 'liftAIScore',
                header: () => (
                  <Flex gap="half">
                    <Box>Drift engage score</Box>
                    <Tooltip content="The Drift engage score is powered by Lift AI" side="top">
                      <Icon size="extra-small" icon={faCircleInfo} />
                    </Tooltip>
                  </Flex>
                ),
                cell: ({ liftAIScore, liftAIIntent }) => (
                  <EngageScoreBadge
                    engageScore={liftAIScore ?? '-'}
                    engageLevel={liftAIIntent ?? ''}
                  />
                ),
                disableSort: true,
                size: 150,
                align: 'right'
              },
              {
                value: 'activityCount',
                header: () => (
                  <Flex gap="half">
                    <Box>Activity</Box>
                    <Tooltip content="# of activities" side="top">
                      <Icon size="extra-small" icon={faCircleInfo} />
                    </Tooltip>
                  </Flex>
                ),
                disableSort: true,
                size: 50,
                align: 'right'
              },
              {
                value: 'timeOfLastEngagement',
                header: 'Last active',
                cell: ({ timeOfLastEngagement }) => humanizedRelativeTime(timeOfLastEngagement),
                disableSort: true,
                size: 125
              },
              {
                value: 'endUserId',
                header: '',
                cell: contact => (
                  <ActivityActions
                    endUserData={contact.endUserData}
                    companyData={contact.companyData}
                    actionTypes={[
                      ActionType.EMAIL,
                      ActionType.LINKEDIN,
                      ActionType.OUTREACH,
                      ActionType.SALESLOFT
                    ]}
                  />
                ),
                disableSort: true,
                size: 130
              }
            ]}
            onStateChange={handleTableStateChange}
            pagination={{
              rowTotal: count ?? 0,
              defaultPagination: tableState.pagination
            }}
          />
        )}
    </Flex>
  )
}

export default ContactsTab
